import { forwardRef, type ReactNode, type Ref } from 'react';
import { format, parseISO } from 'date-fns';
import { Cart, Cart2, Trash2 } from '@hungryroot/icons';
import { Button } from '@hungryroot/ramen';
import { type Order } from '@hungryroot/rest';
import styles from './CartStatusButton.module.css';

export enum Variant {
  Added,
  Removed,
  NoChanges,
  UnsavedChanges,
  Skipped,
  BelowSaveThreshold,
}

export interface Props {
  testid?: string;
  order: Pick<Order, 'preferred_date'>;
  variant: {
    type: Variant;
  };
  onClick?: () => void;
  cartButtonAnimationClassName?: string;
}

function getIconAndText(props: Props): {
  icon: ReactNode;
  text: string;
  style: 'muted' | 'success' | 'default';
  prominence: 'primary' | 'secondary';
} {
  const { order, variant, cartButtonAnimationClassName } = props;

  switch (variant.type) {
    case Variant.BelowSaveThreshold: {
      const formattedDeliverDate = format(
        parseISO(order.preferred_date),
        'MMM d'
      );
      return {
        text: formattedDeliverDate,
        icon: <Cart className={cartButtonAnimationClassName} />,
        style: 'muted',
        prominence: 'secondary',
      };
    }
    case Variant.NoChanges:
    case Variant.UnsavedChanges: {
      const formattedDeliverDate = format(
        parseISO(order.preferred_date),
        'MMM d'
      );

      return {
        text: formattedDeliverDate,
        icon: <Cart className={cartButtonAnimationClassName} />,
        style: variant.type === Variant.NoChanges ? 'muted' : 'default',
        prominence: 'primary',
      };
    }
    case Variant.Added: {
      return {
        text: 'Added',
        icon: <Cart2 />,
        style: 'success',
        prominence: 'primary',
      };
    }
    case Variant.Removed: {
      return {
        text: 'Removed',
        icon: <Trash2 />,
        style: 'success',
        prominence: 'primary',
      };
    }
    case Variant.Skipped: {
      const formattedDeliverDate = format(
        parseISO(order.preferred_date),
        'MMM d'
      );
      return {
        text: formattedDeliverDate,
        icon: <Cart />,
        style: 'default',
        prominence: 'secondary',
      };
    }
  }
}

export const CartButtonReactive = forwardRef(
  (props: Props, ref: Ref<HTMLButtonElement>) => {
    const { onClick, testid } = props;
    const { icon, text, style, prominence } = getIconAndText(props);

    return (
      <Button
        testid={testid}
        ref={ref}
        className={styles.button}
        onClick={onClick}
        size="small-with-icon"
        style={style}
        prominence={prominence}
        iconLeft={icon}
      >
        {text}
      </Button>
    );
  }
);
